import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase, AdminNotice } from '../lib/supabase';
import { APP_VERSION } from '../lib/appVersion';
import { subscribeAccountSwitcherBusy, getAccountSwitcherBusy } from '../lib/accountSwitcherState';

// Only these path prefixes count as "on the dashboard". Everything else —
// /login, /register, /change-password, /pending-verification,
// /pending-payment, /suspended, the root school-search page, etc. — must
// never show a notice, even for an already-logged-in user who's briefly
// sitting on one of those screens mid-transition.
const DASHBOARD_PATH_PREFIXES = ['/principal', '/teacher', '/parent', '/student'];

function isOnDashboard(pathname: string): boolean {
  return DASHBOARD_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// Drives NoticeModal. Runs for every logged-in user (not just admins) —
// Notices/Announcements are broadcast from the admin portal out to every
// school, so any role can be on the receiving end.
export function useActiveNotices() {
  const { user } = useAuth();
  const location = useLocation();
  const onDashboard = isOnDashboard(location.pathname);
  const [queue, setQueue] = useState<AdminNotice[]>([]);

  // Entering a wrong password in AccountSwitcher's "Add Account"/"Switch"
  // flow never touches the CURRENT account's real session — so while that
  // password modal is open, the original account is still fully
  // authenticated and still sitting on a dashboard route. Without this,
  // a real notice for that original account would pop up right on top of
  // the switcher's own password modal, looking exactly like a notice
  // appearing before login. See lib/accountSwitcherState.ts.
  const [switcherBusy, setSwitcherBusy] = useState(getAccountSwitcherBusy());
  useEffect(() => subscribeAccountSwitcherBusy(setSwitcherBusy), []);

  const fetchActive = useCallback(async () => {
    if (!user || user.role === 'super_admin' || !onDashboard) {
      // Notices/Announcements are broadcast BY the admin TO schools — the
      // admin sending them shouldn't also be shown them as a recipient.
      // Also skip the query entirely while not on a real dashboard route,
      // so login/change-password/pending screens neither show nor pay for
      // a notices fetch.
      setQueue([]);
      return;
    }
    const now = new Date().toISOString();

    const { data: notices } = await supabase
      .from('notices')
      .select('*')
      .not('sent_at', 'is', null)
      .lte('sent_at', now)
      .or(`expires_at.is.null,expires_at.gt.${now}`);
    if (!notices) return;

    const { data: reads } = await supabase
      .from('notice_reads')
      .select('notice_id, acknowledged_at')
      .eq('user_id', user.id);
    const ackMap = new Map((reads ?? []).map((r) => [r.notice_id, r.acknowledged_at]));

    const pending = (notices as AdminNotice[]).filter((n) => {
      const inTargetScope = !n.target_school_ids || (!!user.school_id && n.target_school_ids.includes(user.school_id));
      if (!inTargetScope) return false;
      return n.behavior === 'persistent_version'
        ? !!n.target_version && n.target_version !== APP_VERSION
        : !ackMap.get(n.id);
    });

    setQueue(pending);
  }, [user, onDashboard]);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  // For acknowledge/link notices, this marks it done forever. For a
  // persistent_version notice, it only logs that it was seen — it keeps
  // reappearing on future app opens until APP_VERSION catches up, which is
  // the whole point (see appVersion.ts).
  const resolve = useCallback(
    async (notice: AdminNotice) => {
      if (!user) return;
      await supabase.from('notice_reads').upsert({
        notice_id: notice.id,
        user_id: user.id,
        seen_at: new Date().toISOString(),
        acknowledged_at: notice.behavior === 'persistent_version' ? null : new Date().toISOString(),
      });
      setQueue((q) => q.filter((n) => n.id !== notice.id));
    },
    [user]
  );

  // Still fetched and queued as normal above — just held back from
  // display while the account switcher's own password modal is open, so
  // the two overlays never collide. It'll show as soon as the switcher
  // closes (cancel, error, or the full-page reload on a successful
  // switch, which re-mounts everything and re-fetches for whichever
  // account ends up active).
  return { current: switcherBusy ? null : queue[0] ?? null, resolve };
}
